import { useEffect, useState } from 'react';
import { sanityEnabled, sanityFetch, urlFor } from './sanity';
import { defaultContent } from './siteContent';

const QUERY = `{
  "siteSettings": *[_type == "siteSettings"][0]{
    siteName, contactEmail, phone, location
  },
  "seo": *[_type == "seo"][0]{
    title, description, ogImage
  },
  "hero": *[_type == "hero"][0]{
    eyebrow, heading, subheading, paragraph,
    primaryCtaLabel, secondaryCtaLabel,
    trustChips,
    image
  },
  "servicesSection": *[_type == "servicesSection"][0]{
    eyebrow, title, subtitle,
    items[]{ name, description, accentImage }
  },
  "aboutSection": *[_type == "aboutSection"][0]{
    eyebrow, title, subtitle, paragraph,
    image,
    pillars[]{ title, description },
    certTags
  },
  "projectsSection": *[_type == "projectsSection"][0]{
    eyebrow, title, subtitle,
    items[]{ name, category, image, ariaLabel }
  },
  "gallerySection": *[_type == "gallerySection"][0]{
    eyebrow, title, subtitle,
    items[]{ alt, image }
  },
  "footer": *[_type == "footer"][0]{
    brandDescription,
    serviceLinks,
    socialLinks[]{ label, url }
  }
}`;

function normalize(content) {
  const merged = {
    ...defaultContent,
    ...content,
  };

  const heroImageUrl = content?.hero?.image ? urlFor(content.hero.image).width(1600).quality(80).url() : null;
  const ogImageUrl = content?.seo?.ogImage ? urlFor(content.seo.ogImage).width(1200).height(630).fit('crop').quality(80).url() : null;

  return {
    ...merged,
    seo: {
      ...defaultContent.seo,
      ...(content?.seo || {}),
      ogImageUrl,
    },
    hero: {
      ...defaultContent.hero,
      ...(content?.hero || {}),
      imageUrl: heroImageUrl || defaultContent.hero.imageUrl,
    },
    servicesSection: {
      ...defaultContent.servicesSection,
      ...(content?.servicesSection || {}),
      items: (content?.servicesSection?.items || defaultContent.servicesSection.items).map((svc) => ({
        ...svc,
        accentImageUrl: svc.accentImage ? urlFor(svc.accentImage).width(900).quality(80).url() : null,
      })),
    },
    aboutSection: {
      ...defaultContent.aboutSection,
      ...(content?.aboutSection || {}),
      imageUrl: content?.aboutSection?.image
        ? urlFor(content.aboutSection.image).width(1400).quality(80).url()
        : defaultContent.aboutSection.imageUrl,
    },
    projectsSection: {
      ...defaultContent.projectsSection,
      ...(content?.projectsSection || {}),
      items: (content?.projectsSection?.items || defaultContent.projectsSection.items).map((p) => ({
        ...p,
        imageUrl: p.image ? urlFor(p.image).width(1400).quality(80).url() : p.imageUrl,
      })),
    },
    gallerySection: {
      ...defaultContent.gallerySection,
      ...(content?.gallerySection || {}),
      items: (content?.gallerySection?.items || defaultContent.gallerySection.items).map((g) => ({
        ...g,
        imageUrl: g.image ? urlFor(g.image).width(1400).quality(80).url() : g.imageUrl,
      })),
    },
    footer: {
      ...defaultContent.footer,
      ...(content?.footer || {}),
    },
  };
}

export function useCmsContent() {
  const [content, setContent] = useState(defaultContent);
  const [status, setStatus] = useState(sanityEnabled ? 'loading' : 'disabled');

  useEffect(() => {
    let cancelled = false;
    async function run() {
      if (!sanityEnabled) return;
      try {
        const data = await sanityFetch(QUERY);
        if (cancelled) return;
        setContent(normalize(data || {}));
        setStatus('ready');
      } catch (e) {
        if (cancelled) return;
        setStatus('error');
      }
    }
    run();
    return () => {
      cancelled = true;
    };
  }, []);

  return { content, status, sanityEnabled };
}

